import { useState, useEffect, useRef } from 'react';

export const checkInternetConnection = async (timeout: number = 8000): Promise<boolean> => {
  if (!navigator.onLine) {
      return false;
  }

  const endpoints = [
      'https://www.google.com/favicon.ico',
      'https://httpbin.org/status/200',
      'https://www.cloudflare.com/favicon.ico'
  ];

  for (const endpoint of endpoints) {
    try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    await fetch(endpoint, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache',
        signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return true; 
    } catch {
    continue; 
    }
  }

return false; 
};


export const onConnectionChange = (callback: (isOnline: boolean) => void) => {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

// Quick check (just browser status)
export const isOnline = (): boolean => navigator.onLine;

// Custom hook that handles all the connection checking logic
export const useInternetConnection = (
    checkInterval: number = 4000,
    onConnectionChanged?: (isConnected: boolean) => void 
  ) => {
    const [isConnected, setIsConnected] = useState(isOnline());
    const onConnectionChangedRef = useRef(onConnectionChanged);  
    
    // Keep callback ref updated
    useEffect(() => {
      onConnectionChangedRef.current = onConnectionChanged; 
    }, [onConnectionChanged]); 
  
    useEffect(() => {
      const checkAndUpdateConnection = async () => {
        const browserOnline = isOnline();
        const connected = browserOnline ? await checkInternetConnection(checkInterval) : false;
        
        console.log("browserOnline =", browserOnline, "actuallyConnected =", connected);
        
        setIsConnected(prevConnected => {
          if (prevConnected !== connected) {
            if (onConnectionChangedRef.current) { 
              onConnectionChangedRef.current(connected);
            }
          }
          return connected;
        });
      };
  
      checkAndUpdateConnection();
      
      const cleanup = onConnectionChange(checkAndUpdateConnection);
      
      // Periodic checks
      const intervalId = setInterval(checkAndUpdateConnection, checkInterval);
  
      return () => {
        cleanup();
        clearInterval(intervalId);
      };
    }, [checkInterval]);
  
    return isConnected;
  };